import { enumerable } from '@Common/Core/CoreDecorator';
import { AutoMetricReference, BaseSeries, KPIScopeDefinition, MetricSeries, RecordSeries, TTRecordSeries, TTSeries } from '@InstanceFactory';
import { IBrowseDocumentsEditor } from '@Studio/Dialogs/Wizards/Data/BrowseDocumentsEditor';
import Guid from '../Core/Guid';

import Maybe from '../Core/Maybe';

import documentHelper from './DocumentHelper';
import { AggregationType, StatementType, TypeKind } from '@Common/Enums';

export default class SeriesHelper {
    constructor(private readonly projectId: string) {}

    public static isTTRecordSeries(series: BaseSeries): series is TTRecordSeries {
        return TTRecordSeries.isInstanceOfType(series);
    }

    public static isRecordSeries(series: BaseSeries): series is RecordSeries {
        return RecordSeries.isInstanceOfType(series) && !SeriesHelper.isTTRecordSeries(series);
    }

    public static isMetricSeries(series: BaseSeries): series is MetricSeries {
        return MetricSeries.isInstanceOfType(series);
    }

    public static isTTSeries(series: BaseSeries): series is TTSeries {
        return TTSeries.isInstanceOfType(series);
    }

    public getSeriesType(series: BaseSeries): TypeKind | null {
        const wrapSeries = this.getSeries(series);
        if (!wrapSeries) {
            return null;
        }
        return wrapSeries.type;
    }

    //TODO: merge the same names dimension
    public getMetricDimension(series: BaseSeries[], dimensionId: string): KPIScopeDefinition {
        const metricSeries = series.filter(s => SeriesHelper.isMetricSeries(s) && s.metric) as MetricSeries[];
        const metricIds = metricSeries.map(ms => ms.metric.id);
        return documentHelper.findDimension(this.projectId, metricIds, dimensionId);
    }

    public getBrowseDocumentsFromSeries(series: BaseSeries): IBrowseDocumentsEditor | undefined {
        if (MetricSeries.isInstanceOfType(series) && series.metric) {
            const metric = series.metric;
            const { id, name } = metric;
            if (AutoMetricReference.isInstanceOfType(metric)) {
                const { aggregation, fieldId } = metric;
                return {
                    projectId: this.projectId,
                    documentId: id,
                    documentName: [name, fieldId, AggregationType.getName(aggregation)].join('::'),
                    fieldId: fieldId,
                    aggregationId: aggregation
                };
            } else {
                return {
                    projectId: this.projectId,
                    documentId: id,
                    documentName: name
                };
            }
        } else {
            if (TTSeries.isInstanceOfType(series)) {
                const { translationTable, ttAttributeName } = series;
                if (translationTable && translationTable.id && ttAttributeName) {
                    return {
                        projectId: this.projectId,
                        documentId: translationTable.id,
                        documentName: [translationTable.name, ttAttributeName].join('::'),
                        fieldId: ttAttributeName
                    };
                }
            }
        }
    }

    private getSeries(series: BaseSeries): Series<BaseSeries> | null {
        if (SeriesHelper.isTTRecordSeries(series)) {
            return new TTRSeries(this.projectId, series);
        }
        if (SeriesHelper.isRecordSeries(series)) {
            return new RSeries(this.projectId, series);
        }
        if (SeriesHelper.isMetricSeries(series)) {
            return new MSeries(this.projectId, series);
        }
        if (SeriesHelper.isTTSeries(series)) {
            return new TTSeriesImpl(this.projectId, series);
        }
        return null;
    }
}

abstract class Series<TSeries extends BaseSeries> {
    constructor(protected readonly projectId: string, protected readonly series: TSeries) {}

    abstract get type(): TypeKind | null;
}

class RSeries<TRecordSeries extends RecordSeries> extends Series<TRecordSeries> {
    constructor(projectId: string, series: TRecordSeries) {
        super(projectId, series);
    }

    @enumerable
    get type(): TypeKind | null {
        const fieldType = this.isRecordAggSeries ? TypeKind.int : this.getFieldType();

        return statementToType(this.rollupType, fieldType) || fieldType || TypeKind.string;
    }

    @enumerable
    private get isRecordAggSeries(): boolean {
        return _.isEmpty(this.series.fieldPath);
    }

    @enumerable
    private get rollupType() {
        return Maybe(this.series)
            .read(s => s.rollUpMethod)
            .read(rm => rm.rollUpType)
            .valueOrDefault(StatementType.emptyStatement);
    }

    private getFieldType(): TypeKind | null {
        const rf = documentHelper.findRecordField(this.projectId, this.series.record, { id: this.series.fieldPath });
        const field = rf && rf.field;
        if (!field) {
            return null;
        }
        if (field.ttId === Guid.empty) {
            return rf.field.type;
        }
        return TypeKind.string;
    }
}

class TTRSeries extends RSeries<TTRecordSeries> {
    constructor(projectId: string, series: TTRecordSeries) {
        super(projectId, series);
    }

    @enumerable
    get type(): TypeKind | null {
        if (this.series.translationTable) {
            const ttField = documentHelper.findTTField(this.projectId, this.series.translationTable.id, this.series.ttAttributeName);
            const field = ttField && ttField.field;
            if (field) {
                return field.type;
            }
        }
        return null;
    }
}

class TTSeriesImpl extends Series<TTSeries> {
    constructor(projectId: string, series: TTSeries) {
        super(projectId, series);
    }

    @enumerable
    get type(): TypeKind {
        return TypeKind.string;
    }
}

class MSeries extends Series<MetricSeries> {
    constructor(projectId: string, series: MetricSeries) {
        super(projectId, series);
    }

    @enumerable
    get type(): TypeKind {
        const metric = documentHelper.getMetric(this.projectId, this.series.metric);
        if (!metric) {
            return TypeKind.float;
        }
        return metric.metricType;
    }
}

// eslint-disable-next-line complexity
function statementToType(stmt: StatementType, fieldType: TypeKind | null): TypeKind | null {
    switch (stmt) {
        case StatementType.sortByAndTakeFirstFunctionForEvent:
        case StatementType.firstFunctionForArray:
        case StatementType.firstFunctionForEvent:
        case StatementType.firstFunctionForProcesses:
        case StatementType.firstFunctionForProcessStates:
        case StatementType.firstFunctionForSubprocesses:
        case StatementType.sortByAndTakeLastFunctionForEvent:
        case StatementType.lastFunctionForArray:
        case StatementType.lastFunctionForEvent:
        case StatementType.lastFunctionForProcesses:
        case StatementType.lastFunctionForProcessStates:
        case StatementType.lastFunctionForSubprocesses:
        case StatementType.minimumFunctionForEvent:
        case StatementType.maximumFunctionForEvent:
        case StatementType.sumFunctionForEvent:
        case StatementType.sumFunctionForArray:
        case StatementType.sumFunctionForSubprocesses:
        case StatementType.sumFunctionForProcesses:
        case StatementType.sumFunctionForProcessStates:
            return fieldType;
        case StatementType.numberFunctionForEvent:
        case StatementType.numberFunctionForArray:
        case StatementType.numberFunctionForSubprocesses:
        case StatementType.numberFunctionForProcesses:
        case StatementType.numberFunctionForProcessStates:
        case StatementType.countDistinctFunctionForEvent:
        case StatementType.countDistinctFunctionForSubprocesses:
        case StatementType.countDistinctFunctionForProcesses:
        case StatementType.countDistinctFunctionForProcessStates:
            return TypeKind.int;
        case StatementType.meanFunctionForEvent:
        case StatementType.meanFunctionForArray:
        case StatementType.meanFunctionForSubprocesses:
        case StatementType.meanFunctionForProcesses:
        case StatementType.meanFunctionForProcessStates:
        case StatementType.meanWeightedByFunctionForEvent:
        case StatementType.oneStandartDiviationFunctionForEvent:
        case StatementType.medianFunctionForEvent:
        case StatementType.spreadFunctionForEvent:
            return TypeKind.float;
        case StatementType.displayTextFunctionForEvent:
        case StatementType.displayRangeMinMaxFunctionForEvent:
            return TypeKind.string;
        default:
            return null;
    }
}
